import Widget from '../../Core/widget/Widget.js';
import ColumnStore from '../data/ColumnStore.js';
import WidgetColumn from './WidgetColumn.js';
import ObjectHelper from '../../Core/helper/ObjectHelper.js';

let Chart;

/**
 * @module Grid/column/ChartColumn
 */

/**
 * A column that displays a {@link Chart.widget.Chart} in each cell.
 *
 * Records for the chart are provided by the `field` defined in the column configuration. Values will be read from that
 * field on the grid record model for a given row. The value of the field should be an array of records of chart data,
 * each containing the fields required by the `chart`'s defined `series` and `labels`.
 *
 * By default, the chart will update when its `store` fires a `change` event. To disable auto-sync, set the `autoSync`
 * property of the `chart` config to `false`.
 *
 * ```javascript
 * new Grid({
 *     columns : [
 *         {
 *              type: 'chart',
 *
 *              chart : {
 *                  chartType : 'line',
 *                  series : [{
 *                      field : 'price'
 *                  },{
 *                      field : 'changePct'
 *                  }],
 *                  labels : {
 *                      field : 'symbol'
 *                  }
 *              }
 *         }
 *     ]
 * });
 * ```
 *
 * {@inlineexample Grid/column/ChartColumn.js}
 *
 * {@note}
 * Note that this column requires using thin bundles or thin npm packages in your application, and importing the `chart`
 * bundle / package. It does not work with normal packages, module or umd bundles.
 * {/@note}
 *
 * @extends Grid/column/WidgetColumn
 * @classtype chart
 * @column
 */
export default class ChartColumn extends WidgetColumn {
    //region Config

    static $name = 'ChartColumn';

    static type = 'chart';

    static fields = ['chart', 'chartCls', 'field'];

    static defaultChartConfig = {
        showAxes     : false,
        showTitle    : false,
        showControls : false,
        showTooltips : false,
        interactive  : false,
        showLegend   : false,
        animate      : false,
        chartPadding : 0
    };

    static defaults = {
        /**
         * @hideconfigs widgets
         */

        align : 'center',

        /**
         * CSS class name to add to chart.
         * @config {String}
         * @category Rendering
         */
        chartCls : null,

        /**
         * The Chart widget configuration.
         * @config {Object}
         * @default
         */
        chart : ChartColumn.defaultChartConfig,

        field : null,

        widgets : [{
            type : 'chart'
        }],

        // WidgetColumn does not reuse the same widget for the same cell next time on changes, leading to chart
        // animations. Also reusing during scroll leads to animations
        cacheWidgets : false
    };

    construct(...args) {
        super.construct(...args);

        const
            me         = this,
            modelClass = me.grid?.store.modelClass;

        me.requireChartModule();
        me.widgets[0].cls = me.chartCls;

        if (!me.field) {
            console.warn('ChartColumn MUST be configured with a `field`.');
        }
        else if (modelClass && !modelClass.fieldMap[me.field] && !me.constructor.suppressNoModelFieldWarning) {
            console.warn(me.$$name + ' is configured with a field, but this is not part of your Model `fields` collection.');
        }
    }

    requireChartModule() {
        if (!Chart) {
            Chart = globalThis.bryntum.charts?.Chart;
            if (!Chart) {
                throw new Error(`Missing class Chart; make sure you've imported the Charts module.`);
            }
            Widget.register('chart', Chart);
            if (!this.isDestroyed) {
                this.refreshCells();
            }
        }
    }

    defaultRenderer(renderData) {
        if (!Chart) {
            return;
        }

        const
            result     = super.defaultRenderer(renderData),
            { record } = renderData,
            chart      = renderData.widgets?.[0];

        if (chart && !record.isSpecialRow) {
            chart.setData(this.getChartData(record), !this.grid._refreshFromDataChange);
        }

        return result;
    }

    get chartConfig() {
        return this.chart;
    }

    getChartData(record) {
        return this.field ? record.get(this.field) : [];
    }

    onBeforeWidgetCreate(widgetConfig, renderData) {
        const { size, cellElement } = renderData;

        // Cache width, refreshing cell does not seem to come through here properly
        this.chartWidth = widgetConfig.width = size.width ?? cellElement.offsetWidth;
        this.chartHeight = widgetConfig.height = size.height ?? size.configuredHeight ?? cellElement.offsetHeight;

        return super.onBeforeWidgetCreate(widgetConfig, renderData);
    }

    getWidget(widgetConfig, renderData) {
        ObjectHelper.assign(widgetConfig, ChartColumn.defaultChartConfig, this.chartConfig, {
            width  : this.chartWidth,
            height : this.chartHeight
        });

        this.requireChartModule();
        return super.getWidget(widgetConfig, renderData);
    }

    // When column width changes, make sure the chart resized to match
    internalOnWidthChanged({ width, oldWidth }) {
        if (oldWidth != null) {
            this.grid.rowManager.forEach(row => {
                const chart = row.columnWidgets?.get(this.id)?.[0];
                if (chart) {
                    chart.width = width;
                }
            });
        }
    }
}

ColumnStore.registerColumnType(ChartColumn, true);
